// frontend/src/services/marketplace.service.js
import marketplaceAPI from './marketplace.api.js';
import { 
  NIGERIAN_STATES, 
  getLGAsByState, 
  searchStates, 
  searchLGAs,
  getStateByLGA,
  getStateByName,
  getStateNameById,
  getStateIdByName
} from '../config/nigerianLocations';

const marketplaceService = {
  // Upload business images
  uploadBusinessImages: async (files) => {
    const response = await marketplaceAPI.uploadImages(files);
    return response;
  },

  uploadImage: async (file) => {
    const response = await marketplaceAPI.uploadImage(file);
    return response;
  },

  // Check if user can create business
  checkCanCreateBusiness: async () => {
    try {
      // Check if user already has a business by calling the getUserBusiness endpoint
      const response = await marketplaceAPI.getUserBusiness();
      const hasBusiness = response.data !== null;
      
      if (hasBusiness) {
        return {
          canCreate: false,
          reason: 'ALREADY_HAS_BUSINESS',
          message: 'You already have a business. You can edit it instead.'
        };
      }
      
      // Everyone can create one business
      return {
        canCreate: true,
        reason: null,
        message: null
      };
    } catch (error) {
      console.error('Check can create business error:', error);
      return {
        canCreate: false,
        reason: 'ERROR',
        message: 'Unable to verify business creation eligibility'
      };
    }
  },

  // ============================================
  // NIGERIAN LOCATIONS HELPERS
  // ============================================

  // Get all Nigerian states with their LGAs
  getAllStatesWithLGAs: () => {
    return NIGERIAN_STATES;
  },

  // Get all Nigerian state names
  getAllStates: () => {
    return NIGERIAN_STATES.map(state => state.name);
  },

  // Get all states with their IDs and names (for dropdown/select)
  getStatesForDropdown: () => {
    return NIGERIAN_STATES.map(state => ({
      id: state.id,
      name: state.name,
    }));
  },

  // Get LGAs for a specific state
  getLGAs: (stateName) => {
    return getLGAsByState(stateName);
  },

  // Get LGAs for dropdown/select
  getLGAsForDropdown: (stateName) => {
    const lgas = getLGAsByState(stateName);
    return lgas.map(lga => ({
      id: lga,
      name: lga,
    }));
  },

  // Search states by query
  searchStates: (query) => {
    return searchStates(query);
  },

  // Search LGAs by query (optionally within a specific state)
  searchLGAs: (query, stateName = null) => {
    return searchLGAs(query, stateName);
  },

  // Validate if a state exists
  isValidState: (stateName) => {
    return NIGERIAN_STATES.some(state => state.name === stateName);
  },

  // Validate if an LGA exists for a state
  isValidLGA: (stateName, lgaName) => {
    const state = NIGERIAN_STATES.find(s => s.name === stateName);
    return state ? state.lgas.includes(lgaName) : false;
  },

  // Get state by LGA name
  getStateByLGA: (lgaName) => {
    return getStateByLGA(lgaName);
  },

  // Get state object by name
  getStateByName: (stateName) => {
    return getStateByName(stateName);
  },

  // Get state ID by name
  getStateIdByName: (stateName) => {
    return getStateIdByName(stateName);
  },

  // Get state name by ID
  getStateNameById: (stateId) => {
    return getStateNameById(stateId);
  },

  // Get all LGAs across all states (for global search)
  getAllLGAs: () => {
    const allLGAs = [];
    for (const state of NIGERIAN_STATES) {
      for (const lga of state.lgas) {
        allLGAs.push({
          state: state.name,
          stateId: state.id,
          lga: lga,
        });
      }
    }
    return allLGAs;
  },

  // Get total number of states
  getTotalStates: () => {
    return NIGERIAN_STATES.length;
  },

  // Get total number of LGAs
  getTotalLGAs: () => {
    let total = 0;
    for (const state of NIGERIAN_STATES) {
      total += state.lgas.length;
    }
    return total;
  },

  // Get states with their LGA counts
  getStatesWithCounts: () => {
    return NIGERIAN_STATES.map(state => ({
      ...state,
      lgaCount: state.lgas.length,
    }));
  },

  // Get popular locations for quick selection
  getPopularLocations: () => {
    return [
      { state: 'Lagos', cities: ['Ikeja', 'Lekki', 'Victoria Island', 'Surulere', 'Mainland'] },
      { state: 'Abuja', cities: ['Garki', 'Wuse', 'Maitama', 'Asokoro', 'Gwarinpa'] },
      { state: 'Rivers', cities: ['Port Harcourt', 'Obio-Akpor'] },
      { state: 'Kano', cities: ['Kano Municipal', 'Nassarawa'] },
      { state: 'Oyo', cities: ['Ibadan'] },
      { state: 'Delta', cities: ['Asaba', 'Warri'] },
      { state: 'Edo', cities: ['Benin City'] }
    ];
  },

  // Search locations (states and cities)
  searchLocations: (query) => {
    if (!query || query.length < 2) return [];
    
    const results = [];
    const searchTerm = query.toLowerCase();
    
    NIGERIAN_STATES.forEach(state => {
      // Check if state matches
      if (state.name.toLowerCase().includes(searchTerm)) {
        results.push({
          type: 'state',
          id: state.id,
          name: state.name,
          fullName: state.name
        });
      }
      
      // Check cities in this state
      state.lgas.forEach(city => {
        if (city.toLowerCase().includes(searchTerm)) {
          results.push({
            type: 'city',
            stateId: state.id,
            stateName: state.name,
            name: city,
            fullName: `${city}, ${state.name}`
          });
        }
      });
    });
    
    // Limit results
    return results.slice(0, 20);
  },

  // Validate Nigerian location
  validateNigerianLocation: (stateName, cityName) => {
    if (!stateName) {
      return { isValid: false, error: 'State is required' };
    }
    
    if (!cityName) {
      return { isValid: false, error: 'City/LGA is required' };
    }
    
    const isValidState = marketplaceService.isValidState(stateName);
    if (!isValidState) {
      return { isValid: false, error: 'Please select a valid Nigerian state' };
    }
    
    const isValidLGA = marketplaceService.isValidLGA(stateName, cityName);
    if (!isValidLGA) {
      return { isValid: false, error: 'Please select a valid LGA for the selected state' };
    }
    
    return { isValid: true };
  },

  // Validate business data before submission
  validateBusinessData: (data) => {
    const errors = {};

    if (!data.name || data.name.length < 2) {
      errors.name = 'Business name must be at least 2 characters';
    }

    if (!data.category) {
      errors.category = 'Please select a category';
    }

    if (!data.phone || !/^[0-9]{11}$/.test(data.phone)) {
      errors.phone = 'Please enter a valid 11-digit phone number';
    }

    // Validate state using Nigerian states data
    if (!data.state) {
      errors.state = 'Please select a state';
    } else if (!marketplaceService.isValidState(data.state)) {
      errors.state = 'Please select a valid Nigerian state';
    }

    // Validate city/LGA
    if (!data.city) {
      errors.city = 'Please select a city/LGA';
    } else if (data.state && !marketplaceService.isValidLGA(data.state, data.city)) {
      errors.city = 'Please select a valid LGA for the selected state';
    }

    if (!data.address || !data.address.trim()) {
      errors.address = 'Please enter your business address';
    }

    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (data.website && !/^https?:\/\/.+/.test(data.website)) {
      errors.website = 'Please enter a valid website URL starting with http:// or https://';
    }

    if (data.description && data.description.length > 1000) {
      errors.description = 'Description must be less than 1000 characters';
    }

    if (data.tags && data.tags.length > 10) {
      errors.tags = 'Maximum 10 tags allowed';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Format business data for submission
  formatBusinessData: (formData) => {
    return {
      name: formData.name.trim(),
      description: formData.description?.trim() || null,
      category: formData.category,
      tags: formData.tags || [],
      location: formData.location || `${formData.city}, ${formData.state}`,
      address: formData.address?.trim() || null,
      city: formData.city,
      state: formData.state,
      phone: formData.phone,
      email: formData.email?.trim() || null,
      website: formData.website?.trim() || null,
      logoUrl: formData.logoUrl || null,
      coverImageUrl: formData.coverImageUrl || null,
      images: formData.images || [],
      socialLinks: formData.socialLinks || {}
    };
  },

  // Legacy location methods (kept for backward compatibility)
  getNigerianLocations: () => {
    return {
      states: marketplaceService.getStatesWithCounts().map(state => ({
        id: state.id,
        name: state.name,
        cities: state.lgas,
      })),
    };
  },

  getNigerianStates: () => {
    return marketplaceService.getStatesForDropdown();
  },

  getCitiesByState: (stateId) => {
    const stateName = marketplaceService.getStateNameById(stateId);
    if (stateName) {
      return marketplaceService.getLGAs(stateName);
    }
    return [];
  },

  getStateById: (stateId) => {
    const stateName = marketplaceService.getStateNameById(stateId);
    if (stateName) {
      return marketplaceService.getStateByName(stateName);
    }
    return null;
  }
};

export default marketplaceService;